import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { useAuthToken } from '@/hooks/state/useAuthToken';
import { useSettings } from '@/hooks/state/useSettings';
import { derivePaymentReturnStatus } from '@/pages/subscribe/paymentReturnStatus';

import { downloadStageContextAtom } from './atom';
import {
  clearPendingDownloadIntent,
  readPendingDownloadIntent,
} from './pendingDownloadIntent';
import { useDownloadPurchaseConversion } from './useDownloadPurchaseConversion';
import { useStage } from './useStage';

/**
 * Takes a user who left the download journey for checkout back to the page
 * they were downloading from, and reopens the payment-confirmation fallback
 * (`download.ready`) there, once they reach the `/subscribe` success screen
 * (DEV-6516).
 *
 * The intent stays in storage until the user is back on that page: the
 * success screen's own confirmation (`useConfirmSubscriptionSuccess`) reads it
 * to stand down, so this journey's conversion isn't tracked twice. The page
 * load then clears it like any other.
 *
 * Leaving `/subscribe` with a plain path also drops the payment-return
 * params, so the legacy `useStripeCheckoutResponse` handler never sees them.
 *
 * Gated by the `new-download-flow` flag and the presence of a persisted
 * intent, so it's inert for everyone outside the flagged journey.
 *
 * The handoff is atomic — the journey stashes the intent and navigates
 * straight to `/subscribe`, and a successful payment never routes through any
 * other page. So a page load anywhere else means the journey is over, and the
 * intent is cleared: left behind, it would resurface the download prompt
 * after a later, unrelated checkout (e.g. via the Subscribe CTA, DEV-6727).
 */
export const usePostPaymentDownload = () => {
  const router = useRouter();
  const isDownloadFlowOn = useFeatureIsOn('new-download-flow');
  const { open } = useStage();
  const setContext = useSetAtom(downloadStageContextAtom);
  const { invalidateSettings } = useSettings();
  const { refetchAuthToken } = useAuthToken();
  const fireConversion = useDownloadPurchaseConversion();
  // A ref, not the atom: once handled we must not reopen on the router
  // updates that follow while the navigation back is in flight.
  const hasReopened = useRef(false);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    if (!router.pathname.startsWith('/subscribe')) {
      // Back on the origin page, or the journey was dropped (DEV-6727).
      // Deliberately not flag-gated: a stale intent must die even if the flag
      // flips off.
      clearPendingDownloadIntent();
      return;
    }
    if (hasReopened.current || !isDownloadFlowOn) {
      return;
    }
    const { isOnSuccessScreen } = derivePaymentReturnStatus(router.query);
    if (!isOnSuccessScreen) {
      return;
    }
    const intent = readPendingDownloadIntent();
    if (!intent) {
      return;
    }

    hasReopened.current = true;

    // Pull the just-purchased plan into the session so the re-triggered
    // download carries the paid license rather than the pre-checkout (free)
    // one. Best effort — the webhook may still be landing, but the modal is
    // manual, so entitlement has usually caught up by the time the user clicks.
    void refetchAuthToken();
    invalidateSettings();

    // These users bypass the generic success screen (they return to the page
    // they came from), so the purchase conversion that screen fires wouldn't
    // fire for them — do it here.
    fireConversion();

    // Already back on `returnTo` once the scene opens, so it just closes.
    const { returnTo, ...context } = intent;
    setContext(context);
    void router.replace(returnTo).then(() => open('download.ready'));
  }, [
    router.isReady,
    router.pathname,
    router.query,
    router,
    isDownloadFlowOn,
    open,
    setContext,
    refetchAuthToken,
    invalidateSettings,
    fireConversion,
  ]);
};
